// PostHog Cloud EU provider.
// - Region: EU (eu.i.posthog.com) — LGPD-aligned residency.
// - Autocapture OFF, session recording OFF, surveys OFF, web vitals OFF.
//   We feed PostHog ONLY events that came through our typed pipeline so
//   masking + consent + schema validation always run first.
// - Identification uses the hashed user id from analytics.identify() — raw
//   auth UUIDs never reach PostHog.
//
// Token is a publishable phc_… project token; safe to ship in client code.

import posthog from "posthog-js";
import { metrics } from "../metrics";
import { CONVERSION_EVENTS } from "../schema";
import { getAttributionContext } from "../utm";
import { consent } from "../consent";
import { _markFlagsReady } from "../flags";
import {
  REPLAY_BLOCK_SELECTORS,
  REPLAY_IGNORE_CLASS,
  REPLAY_MASK_CLASS,
  evaluateReplay,
} from "../replay";
import type { EnrichedEvent, IdentifyTraits, PageInfo } from "../types";
import type { Provider } from "./types";

const POSTHOG_TOKEN =
  (import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as string | undefined) ||
  "phc_tV8WDBZUzNp5Y8TvADnkHsf8YeCCsB3UoKk9C5KbnW9K";
const POSTHOG_HOST =
  (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ||
  "https://eu.i.posthog.com";

export class PostHogProvider implements Provider {
  readonly name = "posthog";
  readonly categories = ["analytics"] as const;
  private loaded = false;
  private lastPath: string | null = null;
  private recordingActive = false;

  async load(): Promise<void> {
    if (this.loaded || typeof window === "undefined" || !POSTHOG_TOKEN) {
      this.loaded = true;
      return;
    }
    const t0 = performance.now();
    try {
      posthog.init(POSTHOG_TOKEN, {
        api_host: POSTHOG_HOST,
        // We drive everything explicitly from the typed pipeline.
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_performance: false,
        // Replay is OFF by default — manually started per-route via
        // applyReplayPolicy() after consent + sample + route checks pass.
        disable_session_recording: true,
        // Even when started, recording uses DENY-BY-DEFAULT masking:
        //   - all <input>/<textarea>/<select>/[contenteditable] BLOCKED
        //   - all visible text masked (text → "*")
        //   - iframes (Stripe, gateway widgets) blocked
        //   - PIX / receipts / balances tagged via data-* attrs blocked
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
          maskInputOptions: {
            password: true,
            email: true,
            number: true,
            tel: true,
            search: true,
            text: true,
            url: true,
            color: false,
            date: false,
            "datetime-local": false,
            month: false,
            range: false,
            time: false,
            week: false,
          },
          blockSelector: REPLAY_BLOCK_SELECTORS,
          ignoreClass: REPLAY_IGNORE_CLASS,
          maskTextClass: REPLAY_MASK_CLASS,
          // Don't capture the network payloads — same masking story applies
          // and the typed pipeline already records `web_vital_measured`.
          recordCrossOriginIframes: false,
        } as Record<string, unknown>,
        disable_surveys: true,
        persistence: "localStorage+cookie",
        person_profiles: "identified_only",
        respect_dnt: true,
        sanitize_properties: (props) => {
          const out = { ...props };
          delete out["$ip"];
          return out;
        },
        // We need PostHog's /decide endpoint for feature flags. Keep enabled.
        advanced_disable_decide: false,
        bootstrap: { distinctID: undefined, featureFlags: {} },
        loaded: (ph) => {
          try {
            ph.onFeatureFlags(() => _markFlagsReady());
          } catch {
            /* noop */
          }
        },
      });
      this.loaded = true;
      metrics.providerLoadMs("posthog", Math.round(performance.now() - t0));
    } catch (err) {
      metrics.providerFailure("posthog");
      this.loaded = true;
      // eslint-disable-next-line no-console
      console.warn("[analytics] PostHog load failed:", err);
    }
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  identify(userHash: string, traits?: IdentifyTraits): void {
    if (!this.loaded) return;
    try {
      // userHash is already FNV-folded by analytics.identify().
      posthog.identify(userHash, traits as Record<string, unknown> | undefined);
    } catch {
      metrics.providerFailure("posthog");
    }
  }

  page(page: PageInfo): void {
    if (!this.loaded) return;
    const path = page.current_path;
    if (path === this.lastPath) return;
    this.lastPath = path;
    try {
      posthog.capture("$pageview", {
        $current_url: typeof window !== "undefined" ? window.location.href : path,
        $pathname: path,
        page_title: page.title,
      });
    } catch {
      metrics.providerFailure("posthog");
    }
  }

  track(event: EnrichedEvent): void {
    if (!this.loaded) return;
    try {
      // page_viewed handled by page() above.
      if (event.name === "page_viewed") return;

      const props: Record<string, unknown> = { ...event.payload };

      if (CONVERSION_EVENTS.has(event.name as never)) {
        const attr = getAttributionContext();
        if (attr.first?.source) props["first_touch_source"] = attr.first.source;
        if (attr.last?.source) props["last_touch_source"] = attr.last.source;
        if (attr.session_utm.gclid) props["gclid"] = attr.session_utm.gclid;
        if (attr.session_utm.fbclid) props["fbclid"] = attr.session_utm.fbclid;
        props["$set_once"] = { first_touch_source: attr.first?.source ?? null };
      }

      posthog.capture(event.name, props);
    } catch {
      metrics.providerFailure("posthog");
    }
  }

  alias(from: string, to: string): void {
    if (!this.loaded) return;
    try {
      posthog.alias(to, from);
    } catch {
      /* noop */
    }
  }

  reset(): void {
    if (!this.loaded) return;
    try {
      posthog.reset(true);
    } catch {
      /* noop */
    }
  }

  async shutdown(): Promise<void> {
    if (!this.loaded) return;
    try {
      this.stopReplay();
      posthog.opt_out_capturing();
    } catch {
      /* noop */
    }
  }

  // ---- Sprint 3: session replay control ----
  /**
   * Idempotent route-aware replay gate. Called on every SPA navigation AND
   * on consent state changes. Starts/stops the recorder based on
   * `evaluateReplay()`. Never starts on deny-listed routes; never starts
   * without consent.replay.
   */
  applyReplayPolicy(path: string): void {
    if (!this.loaded) return;
    const decision = evaluateReplay({
      path,
      consentReplay: consent.allows("replay"),
    });
    if (decision.shouldRecord) {
      if (!this.recordingActive) {
        try {
          posthog.startSessionRecording();
          this.recordingActive = true;
        } catch {
          metrics.providerFailure("posthog");
        }
      }
    } else if (this.recordingActive) {
      this.stopReplay();
    }
  }

  stopReplay(): void {
    if (!this.recordingActive) return;
    try {
      posthog.stopSessionRecording();
    } catch {
      /* noop */
    } finally {
      this.recordingActive = false;
    }
  }

  isRecording(): boolean {
    return this.recordingActive;
  }
}

